import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AccountBalance as RevenueIcon,
  Close as CloseIcon,
  Done as DoneIcon,
  Phone as PhoneIcon,
  PictureAsPdf as PdfIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Storefront as SupplierIcon,
  TableView as ExcelIcon,
} from '@mui/icons-material';
import {
  calculateInvoices,
  fetchInvoiceTotals,
  generateInvoices,
  getConfig,
  getDailyRevenue,
  getSupplierInvoices,
  listAllInvoices,
  extendInvoice,
  markInvoicePaid,
  updateConfig,
  type DailyRevenueRow,
  type InvoiceTotals,
  type InvoiceStatus,
  type SubscriptionConfig,
  type SubscriptionInvoice,
  type InvoiceSearchField,
} from '@/api/subscription';
import { adminApi, type AdminTenant, type SoftwareVendor } from '@/api/admin';
import { INDUSTRY_OPTIONS, TENANT_SEARCH_FIELD_OPTIONS } from '@/constants/options';
import { brand } from '@/theme/theme';
import { AdminSearchWithFieldSelect } from '@/components/AdminSearchWithFieldSelect';
import { PaginationFooter } from '@/components';
import { downloadExcelFromBackend } from '@/utils/excelExport';
import { getLogoDataUrl } from '@/utils/logoDataUrl';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function industryLabel(value?: string | null): string {
  if (!value) return '—';
  return INDUSTRY_OPTIONS.find(o => o.value === value)?.label ?? value;
}

function statusChip(status: InvoiceStatus) {
  const map: Record<InvoiceStatus, { label: string; color: string }> = {
    PENDING:        { label: 'Pending',   color: '#e65100' },
    PAID:           { label: 'Paid',      color: '#2e7d32' },
    PARTIALLY_PAID: { label: 'Partial',   color: '#1565c0' },
    CANCELLED:      { label: 'Cancelled', color: '#757575' },
  };
  const m = map[status] ?? { label: status, color: '#757575' };
  return (
    <Chip label={m.label} size="small"
      sx={{ bgcolor: m.color, color: '#fff', fontWeight: 700, fontSize: 11 }} />
  );
}

function fmt(n: number) {
  return `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(iso?: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function periodLabelAdmin(year: number, month: number) {
  return new Date(year, month - 1).toLocaleString('default', { month: 'long', year: 'numeric' });
}

function periodLabel(year: number, month: number) {
  const start = new Date(year, month - 1, 1);
  const end   = new Date(year, month, 0); // last day of month
  const fmt   = (d: Date) => d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
  return `${fmt(start)} – ${fmt(end)}`;
}

// ---------------------------------------------------------------------------
// Print/download helpers
// ---------------------------------------------------------------------------

/** Generate and download a GST invoice PDF for a subscription invoice. */
async function downloadGstInvoice(
  invoice: SubscriptionInvoice,
  tenant: AdminTenant | null,
  gstRate: number,
) {
  const { jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  // jsPDF built-in fonts are Latin-1 only — use "Rs." instead of ₹
  const r = (n: number) =>
    `Rs. ${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const fee    = Number(invoice.invoiceAmount);
  const gst    = fee * gstRate;
  const total  = fee + gst;
  const paid   = Number(invoice.paidAmount);
  const due    = total - paid;
  const cgst   = fee * (gstRate / 2);
  const cgstPct = (gstRate / 2 * 100).toFixed(1);

  const tenantName  = tenant?.name ?? invoice.wholesalerName ?? `Tenant #${invoice.wholesalerTenantId}`;
  const tenantAddr  = [tenant?.address, tenant?.city, tenant?.district, tenant?.state, tenant?.pincode].filter(Boolean).join(', ');
  const tenantGstin = tenant?.gstin;
  const period      = periodLabel(invoice.periodYear, invoice.periodMonth);
  const paidOn      = invoice.status === 'PAID' ? fmtDate(invoice.updatedAt) : '-';

  const logoDataUrl = await getLogoDataUrl().catch(() => null);

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const NAVY: [number,number,number] = [123, 97, 255];
  const GRAY: [number,number,number] = [107, 114, 128];

  // ── Header bar ──
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, W, 30, 'F');
  if (logoDataUrl) {
    doc.addImage(logoDataUrl, 'PNG', 5, 2, 56, 22);
  } else {
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.text('Ardinotech', 10, 13);
  }
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text('GSTIN: 36ABFCA0067L1ZM', 10, 28);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('GST Invoice', W - 10, 12, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(invoice.invoiceNumber, W - 10, 19, { align: 'right' });

  // ── Tenant box ──
  doc.setFillColor(240, 244, 255);
  doc.roundedRect(10, 34, W - 20, tenantGstin || tenantAddr ? 18 : 11, 2, 2, 'F');
  doc.setTextColor(30, 58, 138);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text(tenantName, 14, 41);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(0, 0, 0);
  let tY = 46;
  if (tenantGstin) { doc.text(`GSTIN: ${tenantGstin}`, 14, tY); tY += 4.5; }
  if (tenantAddr)  { doc.text(tenantAddr, 14, tY); }

  const boxBottom = tenantGstin || tenantAddr ? 54 : 47;

  // ── Invoice meta ──
  autoTable(doc, {
    startY: boxBottom,
    body: [
      ['Invoice Number', invoice.invoiceNumber, 'Billing Period', period],
      ['Status',         invoice.status,        'Paid On',        paidOn],
    ],
    theme: 'plain',
    styles: { fontSize: 10, cellPadding: { top: 2.5, bottom: 2.5, left: 2, right: 4 }, textColor: [0, 0, 0] as [number,number,number] },
    columnStyles: {
      0: { textColor: GRAY, cellWidth: 38 },
      1: { fontStyle: 'bold', cellWidth: 60 },
      2: { textColor: GRAY, cellWidth: 28 },
      3: { fontStyle: 'bold' },
    },
  });

  // ── Line items ──
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const y1 = (doc as any).lastAutoTable.finalY + 5;
  autoTable(doc, {
    startY: y1,
    margin: { left: 10, right: 10 },
    head: [['Description', 'HSN/SAC', 'Amount (Rs.)', `CGST ${cgstPct}%`, `SGST ${cgstPct}%`, 'Total (Rs.)']],
    body: [
      [
        { content: 'Platform Subscription Fee' },
        '998314',
        { content: r(fee),   styles: { halign: 'right' as const } },
        { content: r(cgst),  styles: { halign: 'right' as const } },
        { content: r(cgst),  styles: { halign: 'right' as const } },
        { content: r(total), styles: { halign: 'right' as const, fontStyle: 'bold' as const } },
      ],
      [
        { content: 'Total', colSpan: 2, styles: { fontStyle: 'bold' as const } },
        { content: r(fee),   styles: { halign: 'right' as const, fontStyle: 'bold' as const } },
        { content: r(cgst),  styles: { halign: 'right' as const, fontStyle: 'bold' as const } },
        { content: r(cgst),  styles: { halign: 'right' as const, fontStyle: 'bold' as const } },
        { content: r(total), styles: { halign: 'right' as const, fontStyle: 'bold' as const } },
      ],
    ],
    headStyles: { fillColor: [243,244,246] as [number,number,number], textColor: [0,0,0] as [number,number,number], fontSize: 10, fontStyle: 'bold' },
    bodyStyles: { fontSize: 10.5, textColor: [0,0,0] as [number,number,number] },
    alternateRowStyles: { fillColor: [255,255,255] as [number,number,number] },
    columnStyles: {
      0: { cellWidth: 68 },
      1: { cellWidth: 20 },
      2: { cellWidth: 30, halign: 'right' as const },
      3: { cellWidth: 24, halign: 'right' as const },
      4: { cellWidth: 24, halign: 'right' as const },
      5: { cellWidth: 30, halign: 'right' as const },
    },
  });

  // ── Summary ──
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const y2 = (doc as any).lastAutoTable.finalY + 6;
  autoTable(doc, {
    startY: y2,
    body: [
      ['Total Billed', r(total)],
      ['Amount Paid',  r(paid)],
      ['Balance Due',  r(due)],
    ],
    theme: 'plain',
    styles: { fontSize: 11.5, cellPadding: { top: 3, bottom: 3, left: 2, right: 2 }, textColor: [0,0,0] as [number,number,number] },
    columnStyles: {
      0: { textColor: GRAY, cellWidth: 40 },
      1: { fontStyle: 'bold', halign: 'right' as const, cellWidth: 50 },
    },
    tableWidth: 90,
    margin: { left: W - 100 },
  });

  // ── Footer ──
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const y3 = (doc as any).lastAutoTable.finalY + 8;
  doc.setDrawColor(229, 231, 235);
  doc.line(10, y3, W - 10, y3);
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.text(
    `Computer-generated document. | Ardinotech B2B SaaS Platform | ${new Date().toLocaleString('en-IN')}`,
    W / 2, y3 + 5, { align: 'center' },
  );

  const slug = tenantName.replace(/\s+/g, '-');
  doc.save(`invoice-${invoice.invoiceNumber}-${slug}.pdf`);
}

/** Download daily revenue Excel for a supplier in a given month. */
function downloadDailyRevenueExcel(
  rows: DailyRevenueRow[],
  tenant: AdminTenant | null,
  year: number,
  month: number,
  config: SubscriptionConfig | null,
) {
  const tenantName  = tenant?.name ?? 'Supplier';
  const tenantGstin = tenant?.gstin ?? '';
  const tenantCity  = tenant?.city  ?? '';
  const tenantState = tenant?.state ?? '';
  const period       = periodLabel(year, month);
  const totalRevenue = rows.reduce((s, r) => s + Number(r.revenue), 0);
  const totalOrders  = rows.reduce((s, r) => s + r.orderCount, 0);
  const pct          = config ? (config.revenuePercentage * 100).toFixed(4) : '—';
  const totalFee     = rows.reduce((s, r) => s + (config ? Number(r.revenue) * config.revenuePercentage : 0), 0);

  const infoRows: string[][] = [
    ['Supplier', tenantName],
    ...(tenantGstin ? [['GSTIN', tenantGstin]] : []),
    ...([tenantCity, tenantState].filter(Boolean).length ? [['Location', [tenantCity, tenantState].filter(Boolean).join(', ')]] : []),
    ['Period', period],
    ['Revenue %', `${pct}%`],
  ];

  const dataRows = rows.map(r => {
    const day = new Date(r.date).toLocaleDateString('en-IN', { weekday: 'short' });
    const fee = config ? Number(r.revenue) * config.revenuePercentage : 0;
    return [r.date, day, String(r.orderCount), Number(r.revenue).toFixed(2), fee.toFixed(2)];
  });
  dataRows.push(['TOTAL', '', String(totalOrders), totalRevenue.toFixed(2), totalFee.toFixed(2)]);

  downloadExcelFromBackend({
    title: `Daily Revenue Report — ${tenantName} — ${period}`,
    subTitle: infoRows.map(([k, v]) => `${k}: ${v}`).join('  |  '),
    headers: ['Date', 'Day', 'Orders', 'Revenue (Rs.)', 'Subscription Fee (Rs.)'],
    rows: dataRows,
  });
}

/** Generate subscription invoice list as PDF and download it. */
async function downloadSubscriptionReportPdf(
  invoices: SubscriptionInvoice[],
  period: string,
  config: SubscriptionConfig | null,
  gstRate: number,
) {
  const { jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');
  const logoDataUrl = await getLogoDataUrl().catch(() => null);

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });
  const W = doc.internal.pageSize.getWidth();
  const NAVY: [number,number,number] = [123, 97, 255];

  // ── Header bar ──
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, W, 30, 'F');
  if (logoDataUrl) {
    doc.addImage(logoDataUrl, 'PNG', 5, 6, 101, 18);
  } else {
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('Ardinotech', 8, 14);
  }
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text('Subscription Invoice Report', W / 2, 12, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(`Period: ${period}`, W / 2, 20, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text(`Generated: ${new Date().toLocaleString('en-IN')}`, W - 8, 13, { align: 'right' });
  if (config) doc.text(`Rate: ${(config.revenuePercentage * 100).toFixed(4)}%  GST: ${config.gstRate}%`, W - 8, 22, { align: 'right' });

  const r = (n: number) => Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const fmtDisc = (inv: SubscriptionInvoice) => {
    if (inv.discountAmt == null || Number(inv.discountAmt) === 0) return '—';
    return `${r(Number(inv.discountAmt))} (${Number(inv.discountPct).toFixed(0)}%)`;
  };
  const fmtPaidOn = (inv: SubscriptionInvoice) =>
    inv.status === 'PAID' && inv.updatedAt
      ? new Date(inv.updatedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
      : '—';

  const body = invoices.map((inv, i) => {
    const fee     = Number(inv.invoiceAmount);
    const netFee  = inv.netPayable != null ? Number(inv.netPayable) : fee - (Number(inv.discountAmt) || 0);
    const gst     = netFee * gstRate;
    const total   = netFee + gst;
    const due     = total - Number(inv.paidAmount);
    const revPct  = inv.revenuePercentage != null
      ? `${(Number(inv.revenuePercentage) * 100).toFixed(4)}`
      : (Number(inv.totalRevenue) > 0 ? `${(fee / Number(inv.totalRevenue) * 100).toFixed(4)}` : '—');
    return [
      String(i + 1),
      inv.invoiceNumber,
      inv.wholesalerName ?? `#${inv.wholesalerTenantId}`,
      industryLabel(inv.industry),
      periodLabelAdmin(inv.periodYear, inv.periodMonth),
      r(Number(inv.totalRevenue)),
      revPct,
      r(fee),
      fmtDisc(inv),
      r(netFee),
      r(gst),
      r(total),
      r(Number(inv.paidAmount)),
      r(due),
      inv.status,
      fmtPaidOn(inv),
    ];
  });

  const totFee   = invoices.reduce((s, i) => s + Number(i.invoiceAmount), 0);
  const totDisc  = invoices.reduce((s, i) => s + (Number(i.discountAmt) || 0), 0);
  const totNet   = totFee - totDisc;
  const totGst   = totNet * gstRate;
  const totTotal = totNet + totGst;
  const totPaid  = invoices.reduce((s, i) => s + Number(i.paidAmount), 0);
  body.push(['', '', `TOTAL (${invoices.length})`, '', '', '', '', r(totFee), totDisc > 0 ? r(totDisc) : '—', r(totNet), r(totGst), r(totTotal), r(totPaid), r(totTotal - totPaid), '', '']);

  autoTable(doc, {
    startY: 30,
    margin: { left: 5, right: 5 },
    head: [['#', 'Invoice #', 'Supplier', 'Industry', 'Period', 'Revenue', 'Rev%', 'Fee', 'Discount', 'Net Pay.', 'GST', 'Total', 'Paid', 'Due', 'Status', 'Paid On']],
    body,
    headStyles: { fillColor: NAVY, textColor: [255,255,255] as [number,number,number], fontSize: 8, fontStyle: 'bold' },
    bodyStyles: { fontSize: 8, textColor: [0,0,0] as [number,number,number] },
    alternateRowStyles: { fillColor: [245,245,255] as [number,number,number] },
    columnStyles: {
      0:  { cellWidth: 5 },
      1:  { cellWidth: 22 },
      2:  { cellWidth: 44 },
      3:  { cellWidth: 21 },
      4:  { cellWidth: 16 },
      5:  { halign: 'right' as const, cellWidth: 17 },
      6:  { halign: 'right' as const, cellWidth: 11 },
      7:  { halign: 'right' as const, cellWidth: 16 },
      8:  { halign: 'right' as const, cellWidth: 22 },
      9:  { halign: 'right' as const, cellWidth: 16, fontStyle: 'bold' as const },
      10: { halign: 'right' as const, cellWidth: 14 },
      11: { halign: 'right' as const, cellWidth: 16, fontStyle: 'bold' as const },
      12: { halign: 'right' as const, cellWidth: 14 },
      13: { halign: 'right' as const, cellWidth: 13 },
      14: { cellWidth: 14 },
      15: { cellWidth: 16 },
    },
    willDrawCell: (data: import('jspdf-autotable').CellHookData) => {
      if (data.row.index === body.length - 1) {
        doc.setFillColor(...NAVY);
        doc.setTextColor(255, 255, 255);
      }
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const yFinal = (doc as any).lastAutoTable.finalY + 5;
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(8);
  doc.text('Computer-generated report. | Ardinotech B2B SaaS Platform | GSTIN: 36ABFCA0067L1ZM', W / 2, yFinal, { align: 'center' });

  const slug = period.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, '');
  doc.save(`subscription-report-${slug}.pdf`);
}

/** Generate subscription invoice list as Excel and download it. */
async function downloadSubscriptionReportExcel(
  invoices: SubscriptionInvoice[],
  period: string,
  config: SubscriptionConfig | null,
  gstRate: number,
) {
  const r = (n: number) => Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtDiscE = (inv: SubscriptionInvoice) => {
    if (inv.discountAmt == null || Number(inv.discountAmt) === 0) return '—';
    return `${r(Number(inv.discountAmt))} (${Number(inv.discountPct).toFixed(0)}%)`;
  };
  const fmtPaidOnE = (inv: SubscriptionInvoice) =>
    inv.status === 'PAID' && inv.updatedAt
      ? new Date(inv.updatedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
      : '—';
  const rows = invoices.map((inv, i) => {
    const fee    = Number(inv.invoiceAmount);
    const netFee = inv.netPayable != null ? Number(inv.netPayable) : fee - (Number(inv.discountAmt) || 0);
    const gst    = netFee * gstRate;
    const total  = netFee + gst;
    const due    = total - Number(inv.paidAmount);
    const revPct = inv.revenuePercentage != null
      ? `${(Number(inv.revenuePercentage) * 100).toFixed(4)}`
      : (Number(inv.totalRevenue) > 0 ? `${(fee / Number(inv.totalRevenue) * 100).toFixed(4)}` : '—');
    return [
      String(i + 1),
      inv.invoiceNumber,
      inv.wholesalerName ?? `#${inv.wholesalerTenantId}`,
      industryLabel(inv.industry),
      periodLabelAdmin(inv.periodYear, inv.periodMonth),
      r(Number(inv.totalRevenue)),
      revPct,
      r(fee),
      fmtDiscE(inv),
      r(netFee),
      r(gst),
      r(total),
      r(Number(inv.paidAmount)),
      r(due),
      inv.status,
      fmtPaidOnE(inv),
    ];
  });

  const totFee   = invoices.reduce((s, i) => s + Number(i.invoiceAmount), 0);
  const totDisc  = invoices.reduce((s, i) => s + (Number(i.discountAmt) || 0), 0);
  const totNet   = totFee - totDisc;
  const totGst   = totNet * gstRate;
  const totTotal = totNet + totGst;
  const totPaid  = invoices.reduce((s, i) => s + Number(i.paidAmount), 0);
  rows.push(['', '', `TOTAL (${invoices.length})`, '', '', '', '', r(totFee), totDisc > 0 ? r(totDisc) : '—', r(totNet), r(totGst), r(totTotal), r(totPaid), r(totTotal - totPaid), '', '']);

  const rateInfo = config ? `Rate: ${(config.revenuePercentage * 100).toFixed(4)}%  |  GST: ${config.gstRate}%  |  ` : '';
  await downloadExcelFromBackend({
    title: `Subscription Report — ${period}`,
    subTitle: `${rateInfo}Generated: ${new Date().toLocaleDateString('en-IN')}  |  ${invoices.length} invoices`,
    headers: ['#', 'Invoice #', 'Supplier', 'Industry', 'Period', 'Revenue', 'Rev%', 'Fee', 'Discount', 'Net Payable', 'GST', 'Total', 'Paid', 'Due', 'Status', 'Paid On'],
    rows,
  });
}

// ---------------------------------------------------------------------------
// Sorting
// ---------------------------------------------------------------------------

type SortCol = 'wholesalerName' | 'industry' | 'vendorName' | 'period' | 'revenue' | 'fee' | 'due' | 'status';
type SortDir = 'asc' | 'desc';

function sortRows(rows: SubscriptionInvoice[], col: SortCol, dir: SortDir): SubscriptionInvoice[] {
  return [...rows].sort((a, b) => {
    let cmp = 0;
    switch (col) {
      case 'wholesalerName': cmp = (a.wholesalerName ?? '').localeCompare(b.wholesalerName ?? '', undefined, { sensitivity: 'base' }); break;
      case 'industry':       cmp = (a.industry ?? '').localeCompare(b.industry ?? '', undefined, { sensitivity: 'base' }); break;
      case 'vendorName':     cmp = (a.vendorName ?? '').localeCompare(b.vendorName ?? '', undefined, { sensitivity: 'base' }); break;
      case 'period':         cmp = (a.periodYear * 100 + a.periodMonth) - (b.periodYear * 100 + b.periodMonth); break;
      case 'revenue':        cmp = Number(a.totalRevenue)  - Number(b.totalRevenue);  break;
      case 'fee':            cmp = Number(a.invoiceAmount) - Number(b.invoiceAmount); break;
      case 'due':            cmp = Number(a.dueAmount)     - Number(b.dueAmount);     break;
      case 'status':         cmp = a.status.localeCompare(b.status); break;
    }
    return dir === 'asc' ? cmp : -cmp;
  });
}

function SortTH({
  col, label, active, dir, onSort, align, sx,
}: {
  col: SortCol; label: string; active: boolean; dir: SortDir;
  onSort: (c: SortCol) => void; align?: 'right' | 'left'; sx?: object;
}) {
  return (
    <TableCell align={align} sx={{ fontWeight: 700, fontSize: 12, ...sx }}>
      <TableSortLabel active={active} direction={active ? dir : 'asc'} onClick={() => onSort(col)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );
}

// ---------------------------------------------------------------------------
// Supplier Detail Dialog
// ---------------------------------------------------------------------------

function SupplierDetailDialog({
  tenantId, onClose, softwareVendors,
}: {
  tenantId: number | null; onClose: () => void; softwareVendors: SoftwareVendor[];
}) {
  const [tenant, setTenant] = useState<AdminTenant | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!tenantId) { setTenant(null); return; }
    setLoading(true);
    adminApi.getTenant(tenantId).then(setTenant).catch(() => setTenant(null)).finally(() => setLoading(false));
  }, [tenantId]);

  const sv = tenant?.softwareVendorId ? softwareVendors.find(v => v.id === tenant.softwareVendorId) : null;
  const addressLine = tenant ? [tenant.address, tenant.city, tenant.district, tenant.state, tenant.pincode].filter(Boolean).join(', ') : '';
  const row = (label: string, value?: string | null) => value ? (
    <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
      <Typography sx={{ fontSize: 12, color: brand.inkSoft, minWidth: 130, flexShrink: 0 }}>{label}</Typography>
      <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{value}</Typography>
    </Box>
  ) : null;

  return (
    <Dialog open={!!tenantId} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: 15, pb: 0.5, pr: 6 }}>
        {loading ? 'Loading…' : (tenant?.name ?? '—')}
        <IconButton size="small" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: '8px !important' }}>
        {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
        : tenant ? (
          <Stack spacing={1.5}>
            <Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 0.75 }}>
                {tenant.tenantType && <Chip size="small" label={tenant.tenantType === 'WHOLESALER' ? 'SUPPLIER' : tenant.tenantType}
                  sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: '#7B61FF', color: '#fff', borderRadius: 0.5, '& .MuiChip-label': { px: 0.75 } }} />}
                {tenant.status && <Chip size="small" label={tenant.status}
                  sx={{ height: 18, fontSize: 10, fontWeight: 700, borderRadius: 0.5,
                    bgcolor: tenant.status === 'ACTIVE' ? '#dcfce7' : tenant.status === 'TRIAL' ? '#dbeafe' : '#fee2e2',
                    color:   tenant.status === 'ACTIVE' ? '#166534' : tenant.status === 'TRIAL' ? '#1e40af' : '#991b1b',
                    '& .MuiChip-label': { px: 0.75 } }} />}
                <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>{tenant.code}</Typography>
              </Box>
              {row('Industry', tenant.industry ? (tenant.industry.charAt(0) + tenant.industry.slice(1).toLowerCase()) : null)}
              {row('GST Number', tenant.gstin)}
            </Box>
            <Divider />
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: brand.inkSoft, mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>Licences</Typography>
              {row('Drug License', tenant.drugLicenseNumber)}
              {row('DL Expiry', tenant.drugLicenseExpiry)}
              {tenant.hasDlDocument && <Typography sx={{ fontSize: 12, color: '#16a34a' }}>✓ DL certificate uploaded</Typography>}
              {row('Food License (FSSAI)', tenant.foodLicenseNumber)}
              {row('Food Expiry', tenant.foodLicenseExpiry)}
            </Box>
            <Divider />
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: brand.inkSoft, mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>Contact</Typography>
              {tenant.phone && <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                <PhoneIcon sx={{ fontSize: 14, color: brand.violet }} />
                <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{tenant.phone}</Typography>
              </Stack>}
              {row('Email', tenant.email)}
              {addressLine && row('Address', addressLine)}
            </Box>
            {sv && (<><Divider /><Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: brand.inkSoft, mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>Software / ERP</Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{sv.softwareName}</Typography>
              <Typography sx={{ fontSize: 12, color: brand.inkSoft }}>{sv.companyName}</Typography>
            </Box></>)}
            {row('Registered', tenant.createdAt ? new Date(tenant.createdAt).toLocaleDateString('en-IN') : undefined)}
          </Stack>
        ) : <Typography sx={{ fontSize: 13, color: brand.inkSoft }}>Could not load supplier details.</Typography>}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Mark Paid Dialog
// ---------------------------------------------------------------------------

function MarkPaidDialog({
  invoice, gstRate, onClose, onDone,
}: {
  invoice: SubscriptionInvoice | null; gstRate: number;
  onClose: () => void; onDone: (updated: SubscriptionInvoice) => void;
}) {
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  useEffect(() => {
    if (invoice) {
      const gst   = Number(invoice.invoiceAmount) * gstRate;
      const total = Number(invoice.invoiceAmount) + gst;
      setAmount(String((total - Number(invoice.paidAmount)).toFixed(2)));
      setError('');
    }
  }, [invoice, gstRate]);

  async function handleSave() {
    if (!invoice) return;
    const val = parseFloat(amount);
    if (isNaN(val) || val <= 0) { setError('Enter a valid positive amount.'); return; }
    setSaving(true); setError('');
    try {
      const updated = await markInvoicePaid(invoice.id, val);
      onDone(updated);
    } catch {
      setError('Failed to record payment. Please try again.');
    } finally { setSaving(false); }
  }

  if (!invoice) return null;
  const gst   = Number(invoice.invoiceAmount) * gstRate;
  const total = Number(invoice.invoiceAmount) + gst;
  const due   = total - Number(invoice.paidAmount);

  return (
    <Dialog open={!!invoice} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: 15, pb: 0.5, pr: 6 }}>
        Mark Payment Received
        <IconButton size="small" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: '12px !important' }}>
        <Typography sx={{ fontSize: 13, color: brand.inkSoft, mb: 1.5 }}>
          Invoice <strong>{invoice.invoiceNumber}</strong> — {invoice.wholesalerName ?? `Tenant #${invoice.wholesalerTenantId}`}
        </Typography>
        <Stack spacing={0.5} sx={{ mb: 2, p: 1.5, bgcolor: '#f8fafc', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          {[
            ['Fee', fmt(Number(invoice.invoiceAmount)), ''],
            ['GST', fmt(gst), ''],
            ['Total', fmt(total), 'bold'],
            ['Already Paid', fmt(Number(invoice.paidAmount)), '#2e7d32'],
            ['Due', fmt(due), '#e65100'],
          ].map(([label, val, style]) => (
            <Box key={String(label)} sx={{ display: 'flex', justifyContent: 'space-between', ...(label === 'Total' ? { borderTop: '1px solid', borderColor: 'divider', pt: 0.5, mt: 0.5 } : {}) }}>
              <Typography sx={{ fontSize: 12, color: style || brand.inkSoft, fontWeight: style === 'bold' || style === '#e65100' || style === '#2e7d32' ? 700 : 400 }}>{label}</Typography>
              <Typography sx={{ fontSize: 12, fontWeight: style === 'bold' || style === '#e65100' || style === '#2e7d32' ? 700 : 400, color: style && style !== 'bold' ? style : 'inherit' }}>{val}</Typography>
            </Box>
          ))}
        </Stack>
        <TextField label="Amount Received (₹)" value={amount} onChange={e => setAmount(e.target.value)}
          size="small" fullWidth type="number" inputProps={{ min: 0, step: '0.01' }}
          helperText="Enter the amount actually received. Partial payments allowed." />
        {error && <Alert severity="error" sx={{ mt: 1.5, fontSize: 12 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
        <Button onClick={onClose} size="small" disabled={saving}>Cancel</Button>
        <Button variant="contained" size="small" disabled={saving} onClick={handleSave}
          startIcon={saving ? <CircularProgress size={13} color="inherit" /> : <DoneIcon />}
          sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}>
          {saving ? 'Saving…' : 'Record Payment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Extend Dialog
// ---------------------------------------------------------------------------

function ExtendDialog({
  invoice, onClose, onDone,
}: {
  invoice: SubscriptionInvoice | null;
  onClose: () => void;
  onDone: (updated: SubscriptionInvoice) => void;
}) {
  const [days,   setDays]   = useState('');
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  useEffect(() => {
    if (invoice) { setDays(String(invoice.extensionDays ?? 0)); setError(''); }
  }, [invoice]);

  async function handleSave() {
    if (!invoice) return;
    const val = parseInt(days, 10);
    if (isNaN(val) || val < 0) { setError('Enter a valid number of days (0 or more).'); return; }
    setSaving(true); setError('');
    try {
      const updated = await extendInvoice(invoice.id, val);
      onDone(updated);
    } catch {
      setError('Failed to save extension. Please try again.');
    } finally { setSaving(false); }
  }

  if (!invoice) return null;
  const blockBase = 32 + (parseInt(days, 10) || 0);
  const daysSince = Math.floor((Date.now() - new Date(invoice.createdAt).getTime()) / 86_400_000);

  return (
    <Dialog open={!!invoice} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: 15, pb: 0.5, pr: 6 }}>
        Grant Extension — {invoice.invoiceNumber}
        <IconButton size="small" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: '12px !important' }}>
        <Typography sx={{ fontSize: 13, color: brand.inkSoft, mb: 2 }}>
          {invoice.wholesalerName ?? `Tenant #${invoice.wholesalerTenantId}`}
          {' '}· Invoice generated {daysSince} day{daysSince !== 1 ? 's' : ''} ago.
        </Typography>
        <TextField
          label="Extension Days"
          value={days}
          onChange={e => setDays(e.target.value)}
          size="small" fullWidth type="number"
          inputProps={{ min: 0, step: 1 }}
          helperText={`Orders will be blocked after ${blockBase} days from invoice generation (32 base + ${parseInt(days,10)||0} extension days).`}
        />
        {error && <Alert severity="error" sx={{ mt: 1.5, fontSize: 12 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
        <Button onClick={onClose} size="small" disabled={saving}>Cancel</Button>
        <Button variant="contained" size="small" disabled={saving} onClick={handleSave}
          startIcon={saving ? <CircularProgress size={13} color="inherit" /> : <DoneIcon />}
          sx={{ bgcolor: brand.violet, '&:hover': { bgcolor: brand.navy } }}>
          {saving ? 'Saving…' : 'Save Extension'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function SubscriptionAdminPage() {
  const [activeTab, setActiveTab] = useState(0);

  // ── Config ──
  const [config,              setConfig]              = useState<SubscriptionConfig | null>(null);
  const [pct,                 setPct]                 = useState('');
  const [ceiling,             setCeiling]             = useState('');
  const [gstRateInput,        setGstRateInput]        = useState('');
  const [discountPctInput,    setDiscountPctInput]    = useState('');
  const [discountMonthsInput, setDiscountMonthsInput] = useState('');
  const [configSaving,        setConfigSaving]        = useState(false);

  useEffect(() => {
    getConfig().then(c => {
      setConfig(c); setPct(String(c.revenuePercentage));
      setCeiling(c.ceilingAmount != null ? String(c.ceilingAmount) : '');
      setGstRateInput(String(c.gstRate));
      setDiscountPctInput(c.newUserDiscountPct != null ? String(c.newUserDiscountPct) : '');
      setDiscountMonthsInput(c.newUserDiscountMonths != null ? String(c.newUserDiscountMonths) : '');
    }).catch(() => {});
  }, []);

  const handleSaveConfig = async () => {
    setConfigSaving(true);
    try {
      const c = await updateConfig({
        revenuePercentage: parseFloat(pct),
        ceilingAmount: ceiling ? parseFloat(ceiling) : null,
        gstRate: gstRateInput ? parseFloat(gstRateInput) : null,
        newUserDiscountPct: discountPctInput ? parseFloat(discountPctInput) : null,
        newUserDiscountMonths: discountMonthsInput ? parseInt(discountMonthsInput, 10) : null,
      });
      setConfig(c); setPct(String(c.revenuePercentage));
      setCeiling(c.ceilingAmount != null ? String(c.ceilingAmount) : '');
      setGstRateInput(String(c.gstRate));
      setDiscountPctInput(c.newUserDiscountPct != null ? String(c.newUserDiscountPct) : '');
      setDiscountMonthsInput(c.newUserDiscountMonths != null ? String(c.newUserDiscountMonths) : '');
    } finally { setConfigSaving(false); }
  };

  const gstRate = config ? config.gstRate / 100 : 0.18;

  const now = new Date();

  // ── Period — default to current month (invoices generated in current month are for previous month) ──
  const [genYear,  setGenYear]  = useState(now.getFullYear());
  const [genMonth, setGenMonth] = useState(now.getMonth() + 1);
  const [fetching,    setFetching]    = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [exportingPdf,   setExportingPdf]   = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);

  const isCurrentMonth = genYear === now.getFullYear() && genMonth === now.getMonth() + 1;
  // Also allow Calculate for next-month picker (billMonth = current month)
  const _nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const isNextMonth = genYear === _nextMonth.getFullYear() && genMonth === _nextMonth.getMonth() + 1;
  const isCalculatable = isCurrentMonth || isNextMonth;

  // Picker holds generation month (Jul); actual billing period is the previous month (Jun).
  const _billingPrev = new Date(genYear, genMonth - 2);
  const billYear  = _billingPrev.getFullYear();
  const billMonth = _billingPrev.getMonth() + 1;

  // ── Invoice tab filters ──
  const [statusFilter,   setStatusFilter]   = useState<InvoiceStatus | ''>('');
  // Search box, industry, and SW vendor are "staged" — none hit the backend
  // until Search is clicked (or Enter is pressed), then combined into one call.
  const [searchInput, setSearchInput] = useState('');
  const [searchField, setSearchField] = useState<InvoiceSearchField>('name');
  const [industryFilter, setIndustryFilter] = useState('');
  const [swVendorFilter, setSwVendorFilter] = useState<number | ''>('');

  const [appliedQuery, setAppliedQuery] = useState('');
  const [appliedSearchField, setAppliedSearchField] = useState<InvoiceSearchField>('name');
  const [appliedIndustry, setAppliedIndustry] = useState('');
  const [appliedVendor, setAppliedVendor] = useState<number | ''>('');

  const [noGstin,        setNoGstin]        = useState(false);
  const [noLicense,      setNoLicense]      = useState(false);
  const [licenseExpired, setLicenseExpired] = useState(false);
  const [page,       setPage]       = useState(0);
  const [invoices,   setInvoices]   = useState<SubscriptionInvoice[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const invoicePageSize = 20;
  const [loading,    setLoading]    = useState(false);
  const [monthTotals, setMonthTotals] = useState<InvoiceTotals | null>(null);

  // ── Supplier tab state ──
  const [tenantSearch,     setTenantSearch]     = useState('');
  const [tenantOptions,    setTenantOptions]    = useState<AdminTenant[]>([]);
  const [tenantLoading,    setTenantLoading]    = useState(false);
  const [selectedTenant,   setSelectedTenant]   = useState<AdminTenant | null>(null);
  const [supplierInvs,     setSupplierInvs]     = useState<SubscriptionInvoice[]>([]);
  const [supplierLoading,  setSupplierLoading]  = useState(false);
  const [supplierSort,     setSupplierSort]     = useState<SortCol>('period');
  const [supplierDir,      setSupplierDir]      = useState<SortDir>('desc');
  const [downloadingRevenue, setDownloadingRevenue] = useState(false);
  // Download month for daily revenue — default to current month
  const [dlYear,  setDlYear]  = useState(now.getFullYear());
  const [dlMonth, setDlMonth] = useState(now.getMonth() + 1);

  // ── Shared ──
  const [softwareVendors,    setSoftwareVendors]    = useState<SoftwareVendor[]>([]);
  const [selectedSupplierId, setSelectedSupplierId] = useState<number | null>(null);
  const [markPaidInvoice,    setMarkPaidInvoice]    = useState<SubscriptionInvoice | null>(null);
  const [extendInvoiceInv,   setExtendInvoiceInv]   = useState<SubscriptionInvoice | null>(null);
  // Tenant detail for PDF use
  const [tenantDetailCache,  setTenantDetailCache]  = useState<Record<number, AdminTenant>>({});

  // ── Sort (invoice tab) ──
  const [sortCol, setSortCol] = useState<SortCol>('period');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  useEffect(() => {
    // Full list for the filter dropdown, not the paginated Vendors table view.
    adminApi.listSoftwareVendors({ size: 1000 }).then(res => setSoftwareVendors(res.content)).catch(() => {});
  }, []);

  // Debounce tenant search
  const tenantDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (tenantDebounce.current) clearTimeout(tenantDebounce.current);
    if (!tenantSearch.trim()) { setTenantOptions([]); return; }
    tenantDebounce.current = setTimeout(() => {
      setTenantLoading(true);
      adminApi.listTenants({ type: 'WHOLESALER', q: tenantSearch, size: 20 })
        .then(res => setTenantOptions(res.content))
        .catch(() => {})
        .finally(() => setTenantLoading(false));
    }, 300);
    return () => { if (tenantDebounce.current) clearTimeout(tenantDebounce.current); };
  }, [tenantSearch]);

  // Load supplier invoices when tenant selected
  useEffect(() => {
    if (!selectedTenant) { setSupplierInvs([]); return; }
    setSupplierLoading(true);
    getSupplierInvoices(selectedTenant.id)
      .then(setSupplierInvs)
      .catch(() => {})
      .finally(() => setSupplierLoading(false));
  }, [selectedTenant]);

  // ── Invoice tab fetch ──
  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    const filterParams = {
      status: statusFilter || undefined, q: appliedQuery || undefined,
      searchField: appliedQuery ? appliedSearchField : undefined,
      industry: appliedIndustry || undefined, softwareVendorId: appliedVendor || undefined,
      noGstin, noLicense, licenseExpired,
      periodYear: billYear, periodMonth: billMonth,
    };
    try {
      const [res, totals] = await Promise.all([
        listAllInvoices({ ...filterParams, page, size: invoicePageSize }),
        fetchInvoiceTotals(filterParams),
      ]);
      setInvoices(res.content); setTotalPages(res.totalPages); setTotalInvoices(res.totalElements); setMonthTotals(totals);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [statusFilter, appliedQuery, appliedSearchField, appliedIndustry, appliedVendor, noGstin, noLicense, licenseExpired, page, billYear, billMonth]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const handleInvoiceSearch = () => {
    setAppliedQuery(searchInput.trim());
    setAppliedSearchField(searchField);
    setAppliedIndustry(industryFilter);
    setAppliedVendor(swVendorFilter);
    setPage(0);
  };

  const handleGet = async () => {
    setFetching(true);
    try {
      await generateInvoices(billYear, billMonth);
      fetchInvoices();
    } finally { setFetching(false); }
  };

  const handleCalculate = async () => {
    setCalculating(true);
    try {
      await calculateInvoices(billYear, billMonth);
      fetchInvoices();
    } finally { setCalculating(false); }
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const all = await listAllInvoices({ periodYear: billYear, periodMonth: billMonth, size: 10000, page: 0 });
      await downloadSubscriptionReportPdf(all.content, curPeriodLabel, config, gstRate);
    } finally { setExportingPdf(false); }
  };

  const handleExportExcel = async () => {
    setExportingExcel(true);
    try {
      const all = await listAllInvoices({ periodYear: billYear, periodMonth: billMonth, size: 10000, page: 0 });
      await downloadSubscriptionReportExcel(all.content, curPeriodLabel, config, gstRate);
    } finally { setExportingExcel(false); }
  };

  const toggleChip = (val: boolean, set: (v: boolean) => void) => { set(!val); setPage(0); };

  const handleMarkPaidDone = (updated: SubscriptionInvoice) => {
    setMarkPaidInvoice(null);
    setInvoices(prev => prev.map(inv => inv.id === updated.id ? { ...inv, ...updated } : inv));
    setSupplierInvs(prev => prev.map(inv => inv.id === updated.id ? { ...inv, ...updated } : inv));
  };

  const handleExtendDone = (updated: SubscriptionInvoice) => {
    setExtendInvoiceInv(null);
    setInvoices(prev => prev.map(inv => inv.id === updated.id ? { ...inv, ...updated } : inv));
    setSupplierInvs(prev => prev.map(inv => inv.id === updated.id ? { ...inv, ...updated } : inv));
  };

  // Load tenant detail for PDF
  const ensureTenantDetail = async (tenantId: number): Promise<AdminTenant | null> => {
    if (tenantDetailCache[tenantId]) return tenantDetailCache[tenantId];
    try {
      const t = await adminApi.getTenant(tenantId);
      setTenantDetailCache(prev => ({ ...prev, [tenantId]: t }));
      return t;
    } catch { return null; }
  };

  const handleDownloadGstInvoice = async (inv: SubscriptionInvoice) => {
    const tenant = await ensureTenantDetail(inv.wholesalerTenantId);
    downloadGstInvoice(inv, tenant, gstRate);
  };

  const handleDownloadDailyRevenue = async () => {
    if (!selectedTenant) return;
    setDownloadingRevenue(true);
    try {
      const rows = await getDailyRevenue(selectedTenant.id, dlYear, dlMonth);
      downloadDailyRevenueExcel(rows, selectedTenant, dlYear, dlMonth, config);
    } catch { /* ignore */ }
    finally { setDownloadingRevenue(false); }
  };

  const handleSupplierSort = (col: SortCol) => {
    if (col === supplierSort) setSupplierDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSupplierSort(col); setSupplierDir('desc'); }
  };

  const displayed         = sortRows(invoices, sortCol, sortDir);
  const displayedSupplier = sortRows(supplierInvs, supplierSort, supplierDir);

  // The picker holds the generation month (e.g. July); invoices cover the previous month (June).
  const curPeriodLabel = periodLabel(billYear, billMonth);

  // helper to build sort handler
  const makeSort = (set: (c: SortCol) => void, setDir: (d: SortDir | ((p: SortDir) => SortDir)) => void, _col: SortCol, cur: SortCol) =>
    (c: SortCol) => { if (c === cur) setDir(d => d === 'asc' ? 'desc' : 'asc'); else { set(c); setDir('asc'); } };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400 }}>

      {/* ── Config panel ── */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap', mb: 3, p: 2,
                 border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: '#fafafa' }}>
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>Revenue %</Typography>
          <TextField size="small" value={pct} onChange={e => setPct(e.target.value)} placeholder="e.g. 0.0004" sx={{ width: 140 }} helperText="Fraction of sales" />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>Ceiling (₹)</Typography>
          <TextField size="small" value={ceiling} onChange={e => setCeiling(e.target.value)} placeholder="No limit" sx={{ width: 140 }} helperText="Max fee/invoice" />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>GST Rate (%)</Typography>
          <TextField size="small" value={gstRateInput} onChange={e => setGstRateInput(e.target.value)} placeholder="18" sx={{ width: 110 }} helperText="Applied on fee" />
        </Box>
        <Box sx={{ borderLeft: '2px solid #e9d5ff', pl: 2, ml: 1 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: brand.violet, mb: 0.5 }}>New User Discount</Typography>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end' }}>
            <Box>
              <Typography sx={{ fontSize: 10, color: 'text.secondary', mb: 0.5 }}>Discount %</Typography>
              <TextField size="small" value={discountPctInput} onChange={e => setDiscountPctInput(e.target.value)} placeholder="e.g. 20" sx={{ width: 100 }} helperText="% off fee" type="number" />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 10, color: 'text.secondary', mb: 0.5 }}>Upto Months</Typography>
              <TextField size="small" value={discountMonthsInput} onChange={e => setDiscountMonthsInput(e.target.value)} placeholder="e.g. 3" sx={{ width: 100 }} helperText="Since reg." type="number" />
            </Box>
          </Box>
        </Box>
        <Button variant="contained" onClick={handleSaveConfig} disabled={configSaving}
          sx={{ mb: 2.5, bgcolor: brand.violet, '&:hover': { bgcolor: brand.navy } }}>
          {configSaving ? 'Saving…' : 'Save Config'}
        </Button>
        {config && (
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 2.5 }}>
            {(config.revenuePercentage * 100).toFixed(4)}% fee
            {config.ceilingAmount != null ? `, ceiling ₹${config.ceilingAmount}` : ''}
            {`, GST ${config.gstRate}%`}
            {config.newUserDiscountPct != null ? `, new user ${config.newUserDiscountPct}% off for ${config.newUserDiscountMonths ?? '?'} mo.` : ''}
          </Typography>
        )}
      </Box>

      {/* ── Period picker + actions (only show Calculate on tab 0) ── */}
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: brand.navy, mr: 0.5 }}>Period:</Typography>
        <TextField size="small" type="number" label="Year" value={genYear}
          onChange={e => { setGenYear(parseInt(e.target.value)); setPage(0); }} sx={{ width: 90 }} />
        <FormControl size="small" sx={{ width: 110 }}>
          <InputLabel>Month</InputLabel>
          <Select value={genMonth} label="Month" onChange={e => { setGenMonth(Number(e.target.value)); setPage(0); }}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
              <MenuItem key={m} value={m}>{new Date(2000, m - 1).toLocaleString('default', { month: 'short' })}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="outlined" size="small" onClick={handleGet} disabled={fetching}
          startIcon={fetching ? <CircularProgress size={13} /> : <RefreshIcon fontSize="small" />}>
          {fetching ? 'Fetching…' : 'Get'}
        </Button>
        {activeTab === 0 && (
          <Tooltip title={isCalculatable ? 'Recalculate fees for this billing period.' : 'Calculate is only available for the current or previous billing month'}>
            <span>
              <Button variant="contained" size="small" color="warning"
                disabled={!isCalculatable || calculating} onClick={handleCalculate}
                startIcon={calculating ? <CircularProgress size={13} color="inherit" /> : undefined}>
                {calculating ? 'Calculating…' : 'Calculate'}
              </Button>
            </span>
          </Tooltip>
        )}
        <Typography sx={{ fontSize: 12, color: brand.inkSoft, ml: 1 }}>
          Period: <strong>{curPeriodLabel}</strong>
        </Typography>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <Button size="small" variant="outlined" color="error"
            startIcon={exportingPdf ? <CircularProgress size={13} color="inherit" /> : <PdfIcon sx={{ fontSize: 15 }} />}
            disabled={exportingPdf} onClick={handleExportPdf}
            sx={{ fontSize: 11, textTransform: 'none', height: 30 }}>
            {exportingPdf ? 'Generating…' : 'PDF Report'}
          </Button>
          <Button size="small" variant="outlined" color="success"
            startIcon={exportingExcel ? <CircularProgress size={13} color="inherit" /> : <ExcelIcon sx={{ fontSize: 15 }} />}
            disabled={exportingExcel} onClick={handleExportExcel}
            sx={{ fontSize: 11, textTransform: 'none', height: 30 }}>
            {exportingExcel ? 'Generating…' : 'Excel Report'}
          </Button>
        </Box>
      </Box>

      {/* ── Tabs ── */}
      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}
        sx={{ mb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Tab icon={<RevenueIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Monthly Invoices"
          sx={{ fontSize: 13, fontWeight: 600, minHeight: 42, textTransform: 'none' }} />
        <Tab icon={<SupplierIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Supplier Invoices"
          sx={{ fontSize: 13, fontWeight: 600, minHeight: 42, textTransform: 'none' }} />
      </Tabs>

      {/* ══════════════════════════════════════════════════════════════════
          TAB 0 — MONTHLY INVOICES
      ══════════════════════════════════════════════════════════════════ */}
      {activeTab === 0 && (
        <>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 1.5 }}>
            <AdminSearchWithFieldSelect
              field={searchField}
              onFieldChange={v => setSearchField(v as InvoiceSearchField)}
              options={TENANT_SEARCH_FIELD_OPTIONS}
              value={searchInput}
              onChangeText={setSearchInput}
              onSearch={handleInvoiceSearch}
              placeholder="Search supplier…"
            />
            <FormControl size="small" sx={{ minWidth: 155 }}>
              <InputLabel>Industry</InputLabel>
              <Select value={industryFilter} label="Industry" onChange={e => setIndustryFilter(e.target.value)}>
                <MenuItem value="">All Industries</MenuItem>
                {INDUSTRY_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 175 }}>
              <InputLabel>SW Vendor</InputLabel>
              <Select value={swVendorFilter} label="SW Vendor" onChange={e => setSwVendorFilter(e.target.value as number | '')}>
                <MenuItem value="">All</MenuItem>
                {softwareVendors.map(sv => (
                  <MenuItem key={sv.id} value={sv.id}>
                    <Box>
                      <Typography sx={{ fontSize: 13, lineHeight: 1.2 }}>{sv.softwareName}</Typography>
                      <Typography sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.2 }}>{sv.companyName}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 135 }}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={e => { setStatusFilter(e.target.value as InvoiceStatus | ''); setPage(0); }}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="PAID">Paid</MenuItem>
                <MenuItem value="PARTIALLY_PAID">Partially Paid</MenuItem>
                <MenuItem value="CANCELLED">Cancelled</MenuItem>
              </Select>
            </FormControl>
            <Chip label="No GST"         onClick={() => toggleChip(noGstin,       setNoGstin)}       variant={noGstin       ? 'filled' : 'outlined'} color={noGstin       ? 'warning' : 'default'} size="small" sx={{ fontWeight: 600 }} />
            <Chip label="No License"     onClick={() => toggleChip(noLicense,     setNoLicense)}     variant={noLicense     ? 'filled' : 'outlined'} color={noLicense     ? 'warning' : 'default'} size="small" sx={{ fontWeight: 600 }} />
            <Chip label="License Expired" onClick={() => toggleChip(licenseExpired, setLicenseExpired)} variant={licenseExpired ? 'filled' : 'outlined'} color={licenseExpired ? 'error'   : 'default'} size="small" sx={{ fontWeight: 600 }} />
            <Tooltip title="Refresh"><IconButton size="small" onClick={fetchInvoices} disabled={loading}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
          </Box>

          <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 1300 }}>
              <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Invoice #</TableCell>
                  <SortTH col="wholesalerName" label="Supplier"  active={sortCol === 'wholesalerName'} dir={sortDir} onSort={makeSort(setSortCol, setSortDir, 'wholesalerName', sortCol)} sx={{ minWidth: 200 }} />
                  <SortTH col="industry"       label="Industry"  active={sortCol === 'industry'}       dir={sortDir} onSort={makeSort(setSortCol, setSortDir, 'industry', sortCol)} sx={{ width: 120 }} />
                  <SortTH col="vendorName"     label="SW Vendor" active={sortCol === 'vendorName'}     dir={sortDir} onSort={makeSort(setSortCol, setSortDir, 'vendorName', sortCol)} sx={{ width: 130 }} />
                  <SortTH col="period"         label="Period"    active={sortCol === 'period'}         dir={sortDir} onSort={makeSort(setSortCol, setSortDir, 'period', sortCol)} sx={{ width: 85 }} />
                  <SortTH col="revenue"        label="Revenue"   active={sortCol === 'revenue'}        dir={sortDir} onSort={makeSort(setSortCol, setSortDir, 'revenue', sortCol)} align="right" sx={{ width: 115 }} />
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="right">Rev%</TableCell>
                  <SortTH col="fee"            label="Fee"       active={sortCol === 'fee'}            dir={sortDir} onSort={makeSort(setSortCol, setSortDir, 'fee', sortCol)} align="right" sx={{ width: 95 }} />
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="right">Discount Amt</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="right">Net Payable</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="right">GST</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="right">Total</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="right">Paid</TableCell>
                  <SortTH col="due"    label="Due"    active={sortCol === 'due'}    dir={sortDir} onSort={makeSort(setSortCol, setSortDir, 'due', sortCol)} align="right" sx={{ width: 95 }} />
                  <SortTH col="status" label="Status" active={sortCol === 'status'} dir={sortDir} onSort={makeSort(setSortCol, setSortDir, 'status', sortCol)} sx={{ width: 90 }} />
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Paid On</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={16} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
                ) : displayed.length === 0 ? (
                  <TableRow><TableCell colSpan={16} align="center" sx={{ py: 4, color: 'text.secondary', fontSize: 13 }}>No invoices found for {curPeriodLabel}</TableCell></TableRow>
                ) : (
                  <>
                    {displayed.map(inv => {
                      const locationLine = [inv.city, inv.state].filter(Boolean).join(', ');
                      const fee       = Number(inv.invoiceAmount);
                      const discPct   = inv.discountPct != null ? Number(inv.discountPct) : null;
                      const discAmt   = inv.discountAmt != null ? Number(inv.discountAmt) : (discPct != null ? fee * discPct / 100 : null);
                      const netFee    = inv.netPayable  != null ? Number(inv.netPayable)  : (discAmt != null ? fee - discAmt : fee);
                      const gst   = netFee * gstRate;
                      const total = netFee + gst;
                      const due   = total - Number(inv.paidAmount);
                      const canMarkPaid = inv.status !== 'PAID' && inv.status !== 'CANCELLED';
                      return (
                        <TableRow key={inv.id} hover>
                          <TableCell sx={{ fontSize: 12 }}>{inv.invoiceNumber}</TableCell>
                          <TableCell onClick={() => setSelectedSupplierId(inv.wholesalerTenantId)} sx={{ cursor: 'pointer' }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: brand.violet, lineHeight: 1.3, '&:hover': { textDecoration: 'underline' } }}>
                              {inv.wholesalerName ?? inv.wholesalerTenantId}
                            </Typography>
                            {locationLine && <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>📍 {locationLine}</Typography>}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, color: brand.inkSoft }}>{industryLabel(inv.industry)}</TableCell>
                          <TableCell>
                            {(() => { const sv = softwareVendors.find(v => v.id === inv.softwareVendorId);
                              return sv ? <><Typography sx={{ fontSize: 12, fontWeight: 600, lineHeight: 1.3 }}>{sv.softwareName}</Typography><Typography sx={{ fontSize: 11, color: brand.inkSoft }}>{sv.companyName}</Typography></> : <Typography sx={{ fontSize: 12, color: '#9ca3af' }}>—</Typography>; })()}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{periodLabel(inv.periodYear, inv.periodMonth)}</TableCell>
                          <TableCell sx={{ fontSize: 12 }} align="right">{fmt(Number(inv.totalRevenue))}</TableCell>
                          <TableCell sx={{ fontSize: 11, color: brand.inkSoft }} align="right">{inv.revenuePercentage != null ? `${(Number(inv.revenuePercentage) * 100).toFixed(4)}` : (Number(inv.totalRevenue) > 0 ? `${(fee / Number(inv.totalRevenue) * 100).toFixed(4)}` : '—')}</TableCell>
                          <TableCell sx={{ fontSize: 12 }} align="right">{fmt(fee)}</TableCell>
                          <TableCell align="right">
                            {discAmt != null ? (
                              <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5 }}>
                                <Typography sx={{ fontSize: 12, color: '#7c3aed', fontWeight: 600 }}>{fmt(discAmt)}</Typography>
                                <Typography sx={{ fontSize: 10, color: '#9333ea' }}>({discPct}%)</Typography>
                              </Box>
                            ) : <Typography sx={{ fontSize: 12, color: '#9ca3af' }}>—</Typography>}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, fontWeight: 600, color: brand.navy }} align="right">{fmt(netFee)}</TableCell>
                          <TableCell sx={{ fontSize: 12, color: brand.inkSoft }} align="right">{fmt(gst)}</TableCell>
                          <TableCell sx={{ fontSize: 12, fontWeight: 600 }} align="right">{fmt(total)}</TableCell>
                          <TableCell sx={{ fontSize: 12 }} align="right">{fmt(Number(inv.paidAmount))}</TableCell>
                          <TableCell sx={{ fontSize: 12, color: due > 0 ? '#e65100' : 'inherit', fontWeight: due > 0 ? 700 : 400 }} align="right">{fmt(due)}</TableCell>
                          <TableCell>{statusChip(inv.status)}</TableCell>
                          <TableCell sx={{ fontSize: 12, color: inv.status === 'PAID' ? '#2e7d32' : '#9ca3af', fontWeight: inv.status === 'PAID' ? 600 : 400 }}>
                            {inv.status === 'PAID' ? fmtDate(inv.updatedAt) : '—'}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <Stack direction="row" spacing={0.5} flexWrap="nowrap">
                              {canMarkPaid && (
                                <Tooltip title="Record payment">
                                  <Button size="small" variant="outlined" onClick={() => setMarkPaidInvoice(inv)}
                                    sx={{ fontSize: 10, px: 1, py: 0.3, minWidth: 0, height: 22, color: '#2e7d32', borderColor: '#86efac', bgcolor: '#f0fdf4', textTransform: 'none', '&:hover': { bgcolor: '#dcfce7' } }}>
                                    Mark Paid
                                  </Button>
                                </Tooltip>
                              )}
                              <Tooltip title={`Grant extension days (currently ${inv.extensionDays ?? 0} days)`}>
                                <Button size="small" variant="outlined" onClick={() => setExtendInvoiceInv(inv)}
                                  sx={{ fontSize: 10, px: 1, py: 0.3, minWidth: 0, height: 22, color: '#7c3aed', borderColor: '#c4b5fd', bgcolor: '#f5f3ff', textTransform: 'none', '&:hover': { bgcolor: '#ede9fe' } }}>
                                  +{inv.extensionDays ?? 0}d Ext
                                </Button>
                              </Tooltip>
                              {(inv.status === 'PAID' || inv.status === 'PARTIALLY_PAID') && (
                                <Tooltip title="Download GST Invoice PDF">
                                  <Button size="small" variant="outlined" onClick={() => handleDownloadGstInvoice(inv)}
                                    startIcon={<PdfIcon sx={{ fontSize: '11px !important' }} />}
                                    sx={{ fontSize: 10, px: 1, py: 0.3, minWidth: 0, height: 22, color: '#dc2626', borderColor: '#fca5a5', bgcolor: '#fff5f5', textTransform: 'none', '&:hover': { bgcolor: '#fee2e2' } }}>
                                    GST PDF
                                  </Button>
                                </Tooltip>
                              )}
                              <Tooltip title={`Daily revenue for ${periodLabelAdmin(inv.periodYear, inv.periodMonth)}`}>
                                <Button size="small" variant="outlined"
                                  startIcon={<ExcelIcon sx={{ fontSize: '11px !important', color: '#15803d' }} />}
                                  onClick={async () => {
                                    const tenant = await ensureTenantDetail(inv.wholesalerTenantId);
                                    const rows = await getDailyRevenue(inv.wholesalerTenantId, inv.periodYear, inv.periodMonth);
                                    downloadDailyRevenueExcel(rows, tenant, inv.periodYear, inv.periodMonth, config);
                                  }}
                                  sx={{ fontSize: 10, px: 1, py: 0.3, minWidth: 0, height: 22, color: '#15803d', borderColor: '#86efac', bgcolor: '#f0fdf4', textTransform: 'none', '&:hover': { bgcolor: '#dcfce7' } }}>
                                  Day Rev
                                </Button>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {/* Page total */}
                    {(() => {
                      const pf = displayed.reduce((s, i) => s + Number(i.invoiceAmount), 0);
                      const pg = pf * gstRate; const pt = pf + pg;
                      const pp = displayed.reduce((s, i) => s + Number(i.paidAmount), 0);
                      return (
                        <TableRow sx={{ bgcolor: '#f0f4ff', '& td': { fontWeight: 700, fontSize: 12, borderTop: '2px solid #c7d2fe' } }}>
                          <TableCell colSpan={6} sx={{ color: brand.navy }}>Page Total ({displayed.length})</TableCell>
                          <TableCell /><TableCell /><TableCell align="right">{fmt(pf)}</TableCell>
                          <TableCell align="right" sx={{ color: brand.inkSoft }}>{fmt(pg)}</TableCell>
                          <TableCell align="right">{fmt(pt)}</TableCell>
                          <TableCell align="right">{fmt(pp)}</TableCell>
                          <TableCell align="right">{fmt(pt - pp)}</TableCell>
                          <TableCell /><TableCell /><TableCell />
                        </TableRow>
                      );
                    })()}
                    {monthTotals && (() => {
                      const mf = Number(monthTotals.totalFee); const mg = mf * gstRate; const mt = mf + mg;
                      const mp = Number(monthTotals.totalPaid);
                      return (
                        <TableRow sx={{ bgcolor: '#eef2ff', '& td': { fontWeight: 700, fontSize: 12, borderTop: '1px solid #a5b4fc' } }}>
                          <TableCell colSpan={6} sx={{ color: brand.navy }}>Month Total — {curPeriodLabel} (all pages)</TableCell>
                          <TableCell /><TableCell /><TableCell align="right">{fmt(mf)}</TableCell>
                          <TableCell align="right" sx={{ color: brand.inkSoft }}>{fmt(mg)}</TableCell>
                          <TableCell align="right" sx={{ color: brand.navy }}>{fmt(mt)}</TableCell>
                          <TableCell align="right">{fmt(mp)}</TableCell>
                          <TableCell align="right" sx={{ color: '#e65100' }}>{fmt(mt - mp)}</TableCell>
                          <TableCell /><TableCell /><TableCell />
                        </TableRow>
                      );
                    })()}
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <PaginationFooter
            page={page}
            totalPages={totalPages}
            total={totalInvoices}
            rowsPerPage={invoicePageSize}
            rowsPerPageOptions={[invoicePageSize]}
            onPageChange={setPage}
            onRowsPerPageChange={() => {}}
            label="invoices"
          />
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          TAB 1 — SUPPLIER INVOICES
      ══════════════════════════════════════════════════════════════════ */}
      {activeTab === 1 && (
        <>
          {/* Tenant search */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap', mb: 2, p: 2,
                     border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: '#fafafa' }}>
            <Autocomplete
              sx={{ minWidth: 320 }}
              options={tenantOptions}
              getOptionLabel={opt => opt.name}
              loading={tenantLoading}
              value={selectedTenant}
              onChange={(_, val) => setSelectedTenant(val)}
              inputValue={tenantSearch}
              onInputChange={(_, val) => setTenantSearch(val)}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              renderInput={params => (
                <TextField {...params} size="small" label="Search Supplier / Wholesaler"
                  placeholder="Type name to search…"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: <><SearchIcon sx={{ fontSize: 16, color: brand.inkSoft, mr: 0.5 }} />{params.InputProps.startAdornment}</>,
                    endAdornment: <>{tenantLoading ? <CircularProgress size={14} /> : null}{params.InputProps.endAdornment}</>,
                  }}
                />
              )}
              renderOption={(props, opt) => (
                <li {...props} key={opt.id}>
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: brand.violet }}>{opt.name}</Typography>
                    <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>
                      {[opt.tenantType, opt.industry, opt.city].filter(Boolean).join(' · ')}
                    </Typography>
                  </Box>
                </li>
              )}
            />
            {selectedTenant && (
              <Box sx={{ flex: 1, p: 1.5, bgcolor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1e40af' }}>{selectedTenant.name}</Typography>
                <Typography sx={{ fontSize: 12, color: brand.inkSoft }}>
                  {[selectedTenant.industry && industryLabel(selectedTenant.industry), selectedTenant.city, selectedTenant.state].filter(Boolean).join(' · ')}
                  {selectedTenant.gstin && <> · GSTIN: {selectedTenant.gstin}</>}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Daily revenue download controls */}
          {selectedTenant && (
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 2,
                       p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: '#fafafa' }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: brand.navy }}>
                Download Daily Revenue:
              </Typography>
              <TextField size="small" type="number" label="Year" value={dlYear}
                onChange={e => setDlYear(parseInt(e.target.value))} sx={{ width: 90 }} />
              <FormControl size="small" sx={{ width: 110 }}>
                <InputLabel>Month</InputLabel>
                <Select value={dlMonth} label="Month" onChange={e => setDlMonth(Number(e.target.value))}>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                    <MenuItem key={m} value={m}>{new Date(2000, m - 1).toLocaleString('default', { month: 'short' })}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="outlined" size="small"
                onClick={handleDownloadDailyRevenue} disabled={downloadingRevenue}
                startIcon={downloadingRevenue ? <CircularProgress size={13} /> : <ExcelIcon fontSize="small" sx={{ color: '#15803d' }} />}
                sx={{ borderColor: '#86efac', color: '#15803d', bgcolor: '#f0fdf4', '&:hover': { bgcolor: '#dcfce7' } }}>
                {downloadingRevenue ? 'Downloading…' : 'Download Excel'}
              </Button>
              <Typography sx={{ fontSize: 11, color: brand.inkSoft }}>
                Daily revenue + orders count for {periodLabel(dlYear, dlMonth)}
              </Typography>
            </Box>
          )}

          {!selectedTenant ? (
            <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
              <SupplierIcon sx={{ fontSize: 48, color: '#d1d5db' }} />
              <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
                Search and select a supplier above to view their invoice history.
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: brand.navy }}>
                  {displayedSupplier.length} invoice{displayedSupplier.length !== 1 ? 's' : ''} — {selectedTenant.name}
                </Typography>
                <Tooltip title="Refresh invoices">
                  <IconButton size="small" onClick={() => {
                    setSupplierLoading(true);
                    getSupplierInvoices(selectedTenant.id).then(setSupplierInvs).catch(() => {}).finally(() => setSupplierLoading(false));
                  }} disabled={supplierLoading}><RefreshIcon fontSize="small" /></IconButton>
                </Tooltip>
              </Box>

              <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 1300 }}>
                  <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Invoice #</TableCell>
                      <SortTH col="period"  label="Period"   active={supplierSort === 'period'}  dir={supplierDir} onSort={handleSupplierSort} sx={{ width: 110 }} />
                      <SortTH col="revenue" label="Revenue"  active={supplierSort === 'revenue'} dir={supplierDir} onSort={handleSupplierSort} align="right" sx={{ width: 120 }} />
                      <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="right">Rev%</TableCell>
                      <SortTH col="fee"     label="Fee"      active={supplierSort === 'fee'}     dir={supplierDir} onSort={handleSupplierSort} align="right" sx={{ width: 95 }} />
                      <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="right">Discount Amt</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="right">Net Payable</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="right">GST</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="right">Total</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="right">Paid</TableCell>
                      <SortTH col="due"    label="Due"    active={supplierSort === 'due'}    dir={supplierDir} onSort={handleSupplierSort} align="right" sx={{ width: 95 }} />
                      <SortTH col="status" label="Status" active={supplierSort === 'status'} dir={supplierDir} onSort={handleSupplierSort} sx={{ width: 100 }} />
                      <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Paid On</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {supplierLoading ? (
                      <TableRow><TableCell colSpan={15} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
                    ) : displayedSupplier.length === 0 ? (
                      <TableRow><TableCell colSpan={15} align="center" sx={{ py: 4, color: 'text.secondary', fontSize: 13 }}>No invoices found for this supplier.</TableCell></TableRow>
                    ) : (
                      <>
                        {displayedSupplier.map((inv, idx) => {
                          const fee2      = Number(inv.invoiceAmount);
                          const discPct2  = inv.discountPct != null ? Number(inv.discountPct) : null;
                          const discAmt2  = inv.discountAmt != null ? Number(inv.discountAmt) : (discPct2 != null ? fee2 * discPct2 / 100 : null);
                          const netFee2   = inv.netPayable  != null ? Number(inv.netPayable)  : (discAmt2 != null ? fee2 - discAmt2 : fee2);
                          const gst   = netFee2 * gstRate;
                          const total = netFee2 + gst;
                          const due   = total - Number(inv.paidAmount);
                          const canMarkPaid = inv.status !== 'PAID' && inv.status !== 'CANCELLED';
                          return (
                            <TableRow key={inv.id} hover sx={{ bgcolor: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                              <TableCell sx={{ fontSize: 11, color: '#9ca3af' }}>{idx + 1}</TableCell>
                              <TableCell sx={{ fontSize: 12, fontWeight: 600, color: brand.violet }}>{inv.invoiceNumber}</TableCell>
                              <TableCell sx={{ fontSize: 12 }}>{periodLabel(inv.periodYear, inv.periodMonth)}</TableCell>
                              <TableCell sx={{ fontSize: 13, fontWeight: 700, color: '#152346' }} align="right">{fmt(Number(inv.totalRevenue))}</TableCell>
                              <TableCell sx={{ fontSize: 11, color: brand.inkSoft }} align="right">{inv.revenuePercentage != null ? `${(Number(inv.revenuePercentage) * 100).toFixed(4)}` : (Number(inv.totalRevenue) > 0 ? `${(Number(inv.invoiceAmount) / Number(inv.totalRevenue) * 100).toFixed(4)}` : '—')}</TableCell>
                              <TableCell sx={{ fontSize: 12 }} align="right">{fmt(fee2)}</TableCell>
                              <TableCell align="right">
                                {discAmt2 != null ? (
                                  <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5 }}>
                                    <Typography sx={{ fontSize: 12, color: '#7c3aed', fontWeight: 600 }}>{fmt(discAmt2)}</Typography>
                                    <Typography sx={{ fontSize: 10, color: '#9333ea' }}>({discPct2}%)</Typography>
                                  </Box>
                                ) : <Typography sx={{ fontSize: 12, color: '#9ca3af' }}>—</Typography>}
                              </TableCell>
                              <TableCell sx={{ fontSize: 12, fontWeight: 600, color: brand.navy }} align="right">{fmt(netFee2)}</TableCell>
                              <TableCell sx={{ fontSize: 12, color: brand.inkSoft }} align="right">{fmt(gst)}</TableCell>
                              <TableCell sx={{ fontSize: 12, fontWeight: 600 }} align="right">{fmt(total)}</TableCell>
                              <TableCell sx={{ fontSize: 12, color: '#2e7d32' }} align="right">{fmt(Number(inv.paidAmount))}</TableCell>
                              <TableCell sx={{ fontSize: 12, fontWeight: due > 0 ? 700 : 400, color: due > 0 ? '#e65100' : 'inherit' }} align="right">{fmt(due)}</TableCell>
                              <TableCell>{statusChip(inv.status)}</TableCell>
                              <TableCell sx={{ fontSize: 12, color: inv.status === 'PAID' ? '#2e7d32' : '#9ca3af', fontWeight: inv.status === 'PAID' ? 600 : 400 }}>
                                {inv.status === 'PAID' ? fmtDate(inv.updatedAt) : '—'}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                <Stack direction="row" spacing={0.5} flexWrap="nowrap">
                                  {canMarkPaid && (
                                    <Tooltip title="Record payment">
                                      <Button size="small" variant="outlined" onClick={() => setMarkPaidInvoice(inv)}
                                        sx={{ fontSize: 10, px: 1, py: 0.3, minWidth: 0, height: 22, color: '#2e7d32', borderColor: '#86efac', bgcolor: '#f0fdf4', textTransform: 'none', '&:hover': { bgcolor: '#dcfce7' } }}>
                                        Mark Paid
                                      </Button>
                                    </Tooltip>
                                  )}
                                  <Tooltip title={`Grant extension days (currently ${inv.extensionDays ?? 0} days)`}>
                                    <Button size="small" variant="outlined" onClick={() => setExtendInvoiceInv(inv)}
                                      sx={{ fontSize: 10, px: 1, py: 0.3, minWidth: 0, height: 22, color: '#7c3aed', borderColor: '#c4b5fd', bgcolor: '#f5f3ff', textTransform: 'none', '&:hover': { bgcolor: '#ede9fe' } }}>
                                      +{inv.extensionDays ?? 0}d Ext
                                    </Button>
                                  </Tooltip>
                                  {(inv.status === 'PAID' || inv.status === 'PARTIALLY_PAID') && (
                                    <Tooltip title="Download GST Invoice PDF">
                                      <Button size="small" variant="outlined"
                                        onClick={() => handleDownloadGstInvoice(inv)}
                                        startIcon={<PdfIcon sx={{ fontSize: '11px !important' }} />}
                                        sx={{ fontSize: 10, px: 1, py: 0.3, minWidth: 0, height: 22, color: '#dc2626', borderColor: '#fca5a5', bgcolor: '#fff5f5', textTransform: 'none', '&:hover': { bgcolor: '#fee2e2' } }}>
                                        PDF
                                      </Button>
                                    </Tooltip>
                                  )}
                                  <Tooltip title={`Daily revenue for ${periodLabelAdmin(inv.periodYear, inv.periodMonth)}`}>
                                    <Button size="small" variant="outlined"
                                      startIcon={<ExcelIcon sx={{ fontSize: '11px !important', color: '#15803d' }} />}
                                      onClick={async () => {
                                        if (!selectedTenant) return;
                                        const rows = await getDailyRevenue(selectedTenant.id, inv.periodYear, inv.periodMonth);
                                        downloadDailyRevenueExcel(rows, selectedTenant, inv.periodYear, inv.periodMonth, config);
                                      }}
                                      sx={{ fontSize: 10, px: 1, py: 0.3, minWidth: 0, height: 22, color: '#15803d', borderColor: '#86efac', bgcolor: '#f0fdf4', textTransform: 'none', '&:hover': { bgcolor: '#dcfce7' } }}>
                                      Day Rev
                                    </Button>
                                  </Tooltip>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {/* Supplier totals */}
                        {(() => {
                          const tf = displayedSupplier.reduce((s, i) => s + Number(i.invoiceAmount), 0);
                          const tg = tf * gstRate; const tt = tf + tg;
                          const tp = displayedSupplier.reduce((s, i) => s + Number(i.paidAmount), 0);
                          const td = tt - tp;
                          const tr = displayedSupplier.reduce((s, i) => s + Number(i.totalRevenue), 0);
                          return (
                            <TableRow sx={{ bgcolor: '#eef2ff', '& td': { fontWeight: 700, fontSize: 12, borderTop: '2px solid #a5b4fc' } }}>
                              <TableCell colSpan={3} sx={{ color: brand.navy }}>Total — {displayedSupplier.length} invoice{displayedSupplier.length !== 1 ? 's' : ''}</TableCell>
                              <TableCell align="right" sx={{ color: '#152346' }}>{fmt(tr)}</TableCell>
                              <TableCell />
                              <TableCell align="right">{fmt(tf)}</TableCell>
                              <TableCell /><TableCell align="right">{fmt(tf)}</TableCell>
                              <TableCell align="right" sx={{ color: brand.inkSoft }}>{fmt(tg)}</TableCell>
                              <TableCell align="right">{fmt(tt)}</TableCell>
                              <TableCell align="right" sx={{ color: '#2e7d32' }}>{fmt(tp)}</TableCell>
                              <TableCell align="right" sx={{ color: td > 0 ? '#e65100' : 'inherit' }}>{fmt(td)}</TableCell>
                              <TableCell /><TableCell /><TableCell />
                            </TableRow>
                          );
                        })()}
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </>
      )}

      {/* ── Shared dialogs ── */}
      <SupplierDetailDialog tenantId={selectedSupplierId} onClose={() => setSelectedSupplierId(null)} softwareVendors={softwareVendors} />
      <MarkPaidDialog invoice={markPaidInvoice} gstRate={gstRate} onClose={() => setMarkPaidInvoice(null)} onDone={handleMarkPaidDone} />
      <ExtendDialog invoice={extendInvoiceInv} onClose={() => setExtendInvoiceInv(null)} onDone={handleExtendDone} />
    </Box>
  );
}
